import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { useGet, usePut } from "../../api";
import {
  IKindredDealDetail,
  IKindredDealDomainInput,
  IKindredDealUpdate,
  IKindredEntityUpdateRequest,
  KindredPublishStatus,
  CampaignOverridesFlag,
} from "../../interfaces";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { Edit2, X, Save } from "react-feather";
import { format } from "date-fns";
import {
  BrandTypeahead,
  TagListEditor,
  DealDomainEditor,
  OverrideFlagsEditor,
  OverrideFlagWarning,
  missingOverrides,
  withOverrides,
  DEAL_OVERRIDE_REQUIREMENTS,
} from "../../components/workbench";

/** What each status actually means — asked often enough to belong on the page. */
const STATUS_MEANING: Record<number, string> = {
  [KindredPublishStatus.Draft]:
    "Created but never submitted for approval. Not live to shoppers.",
  [KindredPublishStatus.AwaitingApproval]:
    "Submitted and waiting on a reviewer. Not live to shoppers.",
  [KindredPublishStatus.Approved]:
    "Live to shoppers, provided it is in date and not suspended.",
  [KindredPublishStatus.Deactivated]:
    "Switched off after being live. Not live to shoppers.",
};

const EXCLUSIVE_HINT =
  "This deal's commercial terms are only available through Kindred. It is a campaign-level flag — nothing to do with coupon codes.";

const statusLabel = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting Approval",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: {
      bg: "danger",
      label: "Deactivated",
    },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>{STATUS_MEANING[status] || `Status ${status}`}</Tooltip>
      }
    >
      <Badge bg={info.bg}>{info.label}</Badge>
    </OverlayTrigger>
  );
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm");
  } catch {
    return dateStr;
  }
};

export const KindredDealDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useGet<IKindredDealDetail>(
    `kindred/deals/${id}`,
    true
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<IKindredDealUpdate>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [flagsAcknowledged, setFlagsAcknowledged] = useState(false);
  const [comment, setComment] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const updateDeal = usePut<
    IKindredDealDetail,
    IKindredEntityUpdateRequest<IKindredDealUpdate>
  >();

  useEffect(() => {
    if (data && editing) {
      setForm({
        name: data.name,
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        suspended: data.suspended,
        source: data.source,
        cashbackType: data.cashbackType,
        featured: data.featured,
        featuredLevel2: data.featuredLevel2,
        exclusive: data.exclusive,
        description: data.description,
        shopLink: data.shopLink,
        startDate: data.startDate,
        endDate: data.endDate,
        overridesFlag: data.overridesFlag,
        suspendedReason: data.suspendedReason,
        totalCommission: data.totalCommission,
        merchantFixedCashback: data.merchantFixedCashback,
        domains:
          data.domainDetails?.map((d) => ({
            domain: d.domain,
            validated: d.validated,
          })) || [],
        countries: data.countries || [],
      });
    }
  }, [data, editing]);

  const startEdit = () => {
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
    setComment("");
    setSaveError(null);
    setFlagsAcknowledged(false);
  };

  const buildChanges = (): Partial<IKindredDealUpdate> => {
    const changed: Partial<IKindredDealUpdate> = {};
    for (const key of Object.keys(form) as (keyof IKindredDealUpdate)[]) {
      if (key === "domains" || key === "countries") continue;
      if (
        data &&
        String(form[key] ?? "") !== String((data as any)[key] ?? "")
      ) {
        (changed as any)[key] = form[key];
      }
    }

    // Detect array field changes (domains, countries). A domain's validated
    // flag counts as a change on its own, so compare both fields.
    const origDomains =
      data?.domainDetails?.map((d) => ({
        domain: d.domain,
        validated: d.validated,
      })) || [];
    if (JSON.stringify(form.domains) !== JSON.stringify(origDomains)) {
      changed.domains = form.domains;
    }
    const origCountries = data?.countries || [];
    if (JSON.stringify(form.countries) !== JSON.stringify(origCountries)) {
      changed.countries = form.countries;
    }
    return changed;
  };

  const pendingFlag = Number(form.overridesFlag ?? data?.overridesFlag ?? 0);
  const missingFlags = missingOverrides(
    buildChanges(),
    pendingFlag,
    DEAL_OVERRIDE_REQUIREMENTS
  );

  const handleSave = async () => {
    try {
      setSaveError(null);
      const changed = buildChanges();

      if (Object.keys(changed).length === 0) {
        setSaveError("No changes detected");
        return;
      }
      if (changed.publishedStatus !== undefined) {
        changed.publishedStatus = Number(changed.publishedStatus);
      }
      if (changed.overridesFlag !== undefined) {
        changed.overridesFlag = Number(changed.overridesFlag);
      }
      if (changed.totalCommission !== undefined) {
        changed.totalCommission =
          changed.totalCommission === null
            ? null
            : Number(changed.totalCommission);
      }
      if (changed.merchantFixedCashback !== undefined) {
        changed.merchantFixedCashback =
          changed.merchantFixedCashback === null
            ? null
            : Number(changed.merchantFixedCashback);
      }
      await updateDeal.execute(`kindred/deals/${id}`, {
        data: changed,
        comment,
      });
      setShowSaveModal(false);
      setEditing(false);
      setComment("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await refetch();
    } catch (e: any) {
      setSaveError(e?.message || "Failed to save changes");
    }
  };

  const updateField = (field: keyof IKindredDealUpdate, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" /> Loading deal...
      </div>
    );
  }

  if (error) {
    return <div className="text-danger">Error: {error.message}</div>;
  }

  if (!data) {
    return <div className="text-muted">Deal not found.</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Deal: {data.name}</h4>
        <div className="d-flex gap-2">
          <Link to="/kindred/deals">
            <Button size="sm" variant="outline-secondary">
              ← Back to Deals
            </Button>
          </Link>
          <Link to={`/kindred/deals/${id}/audit`}>
            <Button size="sm" variant="outline-info">
              View Audit Trail
            </Button>
          </Link>
          {!editing ? (
            <Button size="sm" variant="primary" onClick={startEdit}>
              <Edit2 size={14} className="me-1" /> Edit
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cancelEdit}
              >
                <X size={14} className="me-1" /> Cancel
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => setShowSaveModal(true)}
              >
                <Save size={14} className="me-1" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSaveSuccess(false)}
        >
          Deal updated successfully.
        </Alert>
      )}
      {saveError && !showSaveModal && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Campaign Details</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      ID
                    </td>
                    <td>{data.id}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Name</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.name ?? ""}
                          onChange={(e) => updateField("name", e.target.value)}
                        />
                      ) : (
                        data.name
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Brand</td>
                    <td>
                      {editing ? (
                        <BrandTypeahead
                          brandId={form.brandId || data.brandId}
                          brandName={data.brandName}
                          onChange={(brandId) =>
                            updateField("brandId", brandId)
                          }
                        />
                      ) : (
                        <>
                          <Link to={`/kindred/brands/${data.brandId}`}>
                            {data.brandName}
                          </Link>
                          {data.isTopBrand && (
                            <Badge bg="info" className="ms-1">
                              ★
                            </Badge>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Merchant ID</td>
                    <td>
                      {data.merchantId ? (
                        <code>{data.merchantId}</code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                      <div className="small text-muted">
                        The network's own advertiser ID for this campaign.
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Status</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.publishedStatus ?? 0}
                          onChange={(e) =>
                            updateField(
                              "publishedStatus",
                              Number(e.target.value)
                            )
                          }
                        >
                          <option value={0}>Draft</option>
                          <option value={1}>Awaiting Approval</option>
                          <option value={2}>Approved</option>
                          <option value={3}>Deactivated</option>
                        </Form.Select>
                      ) : (
                        statusLabel(data.publishedStatus)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Suspended</td>
                    <td>
                      {editing ? (
                        <>
                          <Form.Check
                            type="switch"
                            checked={form.suspended ?? false}
                            onChange={(e) =>
                              updateField("suspended", e.target.checked)
                            }
                          />
                          <Form.Control
                            size="sm"
                            className="mt-1"
                            placeholder="Suspended reason..."
                            value={form.suspendedReason ?? ""}
                            onChange={(e) =>
                              updateField(
                                "suspendedReason",
                                e.target.value || null
                              )
                            }
                          />
                        </>
                      ) : (
                        <>
                          {data.suspended ? (
                            <Badge bg="danger">Yes</Badge>
                          ) : (
                            <Badge bg="success">No</Badge>
                          )}
                          {data.suspendedAt && (
                            <span className="text-muted ms-2">
                              at {formatDate(data.suspendedAt)}
                            </span>
                          )}
                          {data.suspendedReason && (
                            <div className="small text-muted">
                              {data.suspendedReason}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Source</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.source ?? ""}
                          onChange={(e) =>
                            updateField("source", e.target.value)
                          }
                        />
                      ) : (
                        data.source
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Cashback Type</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.cashbackType ?? ""}
                          onChange={(e) =>
                            updateField("cashbackType", e.target.value)
                          }
                        >
                          <option value="0">Percentage</option>
                          <option value="1">Fixed</option>
                          <option value="2">Click</option>
                          <option value="3">Unknown</option>
                          <option value="4">No Commission</option>
                        </Form.Select>
                      ) : (
                        data.cashbackType
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Total Commission %</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={form.totalCommission ?? ""}
                          onChange={(e) =>
                            updateField(
                              "totalCommission",
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                      ) : (
                        (data.totalCommission ?? "—")
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Fixed Cashback</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={form.merchantFixedCashback ?? ""}
                          onChange={(e) =>
                            updateField(
                              "merchantFixedCashback",
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                      ) : (
                        (data.merchantFixedCashback ?? "—")
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Featured</td>
                    <td>
                      {editing ? (
                        <Form.Check
                          type="switch"
                          checked={form.featuredLevel2 ?? false}
                          onChange={(e) =>
                            updateField("featuredLevel2", e.target.checked)
                          }
                        />
                      ) : data.featuredLevel2 ? (
                        <Badge bg="warning">Featured</Badge>
                      ) : (
                        "No"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">
                      Exclusive
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{EXCLUSIVE_HINT}</Tooltip>}
                      >
                        <span className="text-muted ms-1">ⓘ</span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      {editing ? (
                        <Form.Check
                          type="switch"
                          checked={form.exclusive ?? false}
                          onChange={(e) =>
                            updateField("exclusive", e.target.checked)
                          }
                        />
                      ) : data.exclusive ? (
                        "Yes"
                      ) : (
                        "No"
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Dates & Shop</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      Start Date
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.startDate
                              ? form.startDate.substring(0, 16)
                              : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "startDate",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.startDate)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">End Date</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.endDate ? form.endDate.substring(0, 16) : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "endDate",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.endDate)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Active</td>
                    <td>
                      {data.isActive ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Shop Link</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.shopLink ?? ""}
                          onChange={(e) =>
                            updateField("shopLink", e.target.value)
                          }
                        />
                      ) : data.shopLink ? (
                        <a
                          href={data.shopLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {data.shopLink}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Created</td>
                    <td>{formatDate(data.createdAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Updated</td>
                    <td>{formatDate(data.updatedAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Overrides Flag</td>
                    <td>
                      {editing ? (
                        <OverrideFlagsEditor
                          value={form.overridesFlag ?? 0}
                          onChange={(overridesFlag) =>
                            setForm((prev) => ({ ...prev, overridesFlag }))
                          }
                          flagEnum={CampaignOverridesFlag}
                        />
                      ) : (
                        <OverrideFlagsEditor
                          value={data.overridesFlag}
                          flagEnum={CampaignOverridesFlag}
                          readOnly
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {(data.description || editing) && (
        <Card className="mb-3">
          <Card.Header>Description</Card.Header>
          <Card.Body>
            {editing ? (
              <Form.Control
                as="textarea"
                rows={4}
                value={form.description ?? ""}
                onChange={(e) => updateField("description", e.target.value)}
              />
            ) : (
              <p>{data.description}</p>
            )}
          </Card.Body>
        </Card>
      )}

      <Row>
        <Col md={6}>
          {editing ? (
            <TagListEditor
              label="Countries"
              items={form.countries || []}
              onChange={(countries) =>
                setForm((prev) => ({ ...prev, countries }))
              }
              placeholder="Add country code..."
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Countries ({data.countries?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.countries.length === 0 ? (
                  <span className="text-muted">No countries</span>
                ) : (
                  data.countries.map((c) => (
                    <Badge key={c} bg="light" text="dark" className="me-1 mb-1">
                      {c}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={6}>
          {editing ? (
            <DealDomainEditor
              items={(form.domains as IKindredDealDomainInput[]) || []}
              onChange={(domains) => setForm((prev) => ({ ...prev, domains }))}
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Domains ({data.domainDetails?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.domainDetails.length === 0 ? (
                  <span className="text-muted">No domains</span>
                ) : (
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Validated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.domainDetails.map((d) => (
                        <tr key={d.id}>
                          <td>{d.domain}</td>
                          <td>
                            {d.validated ? (
                              <Badge bg="success">Yes</Badge>
                            ) : (
                              <Badge bg="secondary">No</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Save Comment Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Comment (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the reason for this change..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>
          <OverrideFlagWarning
            missing={missingFlags}
            acknowledged={flagsAcknowledged}
            onAcknowledge={setFlagsAcknowledged}
            onApply={() =>
              updateField(
                "overridesFlag",
                withOverrides(pendingFlag, missingFlags)
              )
            }
          />
          {saveError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {saveError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={
              !comment.trim() ||
              updateDeal.loading ||
              (missingFlags.length > 0 && !flagsAcknowledged)
            }
            onClick={handleSave}
          >
            {updateDeal.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
